/**
 * Profile page – account information (standard N2NAI, loading/error via PageDataState).
 */
import { Box, Typography, Card, CardContent, Avatar, Chip, Divider } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Badge, Info } from '@mui/icons-material'
import type { PermissionLevel } from '../types/api'
import { useGetMeQuery } from '../store/api/authApi'
import { useGetSettingRolesQuery } from '../store/api/settingsApi'
import { PageDataState } from '../components/PageDataState'
import { useAppSelector } from '../hooks/redux'

const ROLE_LABELS: Record<string, string> = {
  user: 'User',
  admin: 'Administrator',
}

const NAV_PERM_KEYS = [
  'purchase',
  'procurement',
  'saleContract',
  'saleControl',
  'factory',
  'virtualWarehouse',
  'futures',
  'position',
  'quality',
  'masterData',
  'setting',
  'factoryBl',
  'factoryNc',
  'factoryNs',
  'factoryNt',
  'factoryPaco',
  'matchingPs',
] as const

const FUNCTION_LABELS: Record<(typeof NAV_PERM_KEYS)[number], string> = {
  purchase: 'Purchase',
  procurement: 'Procurement',
  saleContract: 'Sales Contract',
  saleControl: 'Sale Control',
  factory: 'Factory control',
  virtualWarehouse: 'Virtual Warehouse',
  futures: 'Futures',
  position: 'Position',
  quality: 'Quality',
  masterData: 'Master Data',
  setting: 'Setting',
  factoryBl: 'Bảo Lộc factory',
  factoryNc: 'Nguyên Cường factory',
  factoryNs: 'Nông Sản factory',
  factoryNt: 'Nhơn Trạch factory',
  factoryPaco: 'Pacorini factory',
  matchingPs: 'Matching P-S',
}

function permissionText(level: PermissionLevel | undefined): string {
  if (level === 'Edit') return 'Edit'
  if (level === 'View') return 'View'
  return 'No access'
}

function permissionChipSx(level: PermissionLevel | undefined) {
  if (level === 'Edit') {
    return {
      bgcolor: alpha('#12382f', 0.14),
      color: '#0b261f',
      border: '1px solid rgba(18, 56, 47, 0.22)',
      fontWeight: 700,
    }
  }
  if (level === 'View') {
    return {
      bgcolor: alpha('#c6a24a', 0.18),
      color: '#6b4f12',
      border: '1px solid rgba(198, 162, 74, 0.30)',
      fontWeight: 700,
    }
  }
  return {
    bgcolor: 'rgba(148, 163, 184, 0.14)',
    color: 'rgba(51, 65, 85, 0.95)',
    border: '1px solid rgba(148, 163, 184, 0.28)',
    fontWeight: 700,
  }
}

function ProfilePage() {
  const { data: user, isLoading, error } = useGetMeQuery()
  const permissions = useAppSelector((s) => s.auth.permissions)
  const hasPermissions = Object.keys(permissions).length > 0
  const { data: rolesData, isLoading: rolesLoading } = useGetSettingRolesQuery()

  if (isLoading && !user) return <PageDataState isLoading />
  if (error && !user) return <PageDataState error="Failed to load account information" />
  if (!user) return null

  const displayName = user.full_name || user.username || '—'
  const initial = (displayName !== '—' ? displayName : user.username || 'U').charAt(0).toUpperCase()
  const roleLabel = ROLE_LABELS[user.role] ?? user.role
  const roleName = (() => {
    if (!rolesData?.items) return rolesLoading ? 'Loading...' : roleLabel
    const found = rolesData.items.find((r) => r.roleCodeUper.toUpperCase() === String(user.role ?? '').toUpperCase())
    return found?.roleName ?? roleLabel
  })()

  return (
    <Box sx={{ width: '100%', maxWidth: 640, mx: 'auto' }}>
      <Typography
        variant="h5"
        fontWeight={600}
        gutterBottom
        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' }, mb: 1 }}
      >
        Profile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Account information and role
      </Typography>

      <Card sx={{ borderRadius: 2, boxShadow: 1, mb: 2, overflow: 'hidden' }}>
        <Box
          sx={(theme) => ({
            py: 3,
            px: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          })}
        >
          <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: '1.75rem', mb: 1.5 }}>
            {initial}
          </Avatar>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, mb: 0.5 }}
          >
            {displayName}
          </Typography>
          <Chip
            label={roleLabel}
            size="small"
            icon={<Badge sx={{ fontSize: 16 }} />}
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 600,
              fontSize: '0.8125rem',
            }}
          />
        </Box>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <Info color="action" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.8125rem' }}>
              Account Information
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <InfoRow label="Username" value={user.username} />
            <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            <InfoRow label="Email" value={user.email} />
            <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            <InfoRow label="Full Name" value={user.full_name || '—'} />
            <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            <InfoRow label="Role" value={roleLabel} />
            <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            <InfoRow label="Role Name" value={roleName} />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Info color="action" sx={{ fontSize: 18 }} />
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.8125rem' }}>
                Function Permissions
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 1.5,
                rowGap: 1.25,
              }}
            >
              {NAV_PERM_KEYS.map((key) => {
                const level = permissions[key] as PermissionLevel | undefined
                const text = hasPermissions ? permissionText(level) : 'Loading...'
                return (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      border: '1px solid rgba(23, 35, 29, 0.08)',
                      borderRadius: 1.25,
                      px: 1.25,
                      py: 0.75,
                      backgroundColor: 'rgba(255,255,255,0.6)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                      {FUNCTION_LABELS[key]}
                    </Typography>
                    <Chip
                      size="small"
                      label={text}
                      sx={permissionChipSx(level)}
                      variant={level === 'NoGrant' ? 'outlined' : 'filled'}
                    />
                  </Box>
                )
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>

    </Box>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
        {value}
      </Typography>
    </Box>
  )
}

export default ProfilePage
